import { useQuery } from "@tanstack/react-query";
import { AcademicCapIcon } from "@heroicons/react/24/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import agent, { UsageOverview } from "server";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import { Button, FlatCard } from "components/UI-lib";
import { useHistory } from "react-router-dom";
import { Spinner } from "@kui-react/spinner";
import { displayGpuHours, displayGpuKHours } from "components/utils";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { Input } from "components/Graphs/Input";
import UsageOverviewChart from "./UsageOverviewChart";
import { AdminOrganizationType, Program } from "components/Admin/types";
import {
  DatePicker,
  DateRange,
  DateRangePicker,
} from "components/Graphs/DateRangePicker";
import { PROGRAMS } from "constants/index";

export interface ProgramOverviewProps {
  program: Program;
}
const ProgramOverview: React.FC<ProgramOverviewProps> = ({
  program,
}) => {
  const [cloudAwardDate, setCloudAwardDate] = useState<Date | undefined>();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: firstDay,
      to: now,
    };
  });
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [totalBalance, setTotalBalance] = useState(0);
  const [totalHours, setTotalHours] = useState(0);

  const {
    data: programOrgs,
    error: orgsError,
    isLoading: orgsLoading,
  } = useQuery({
    queryFn: () => getProgramOrgs(),
    queryKey: ["program-orgs", program.id, cloudAwardDate],
  });

  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ["usage", program.id, dateRange],
    queryFn: async () => {
      const usages = await Promise.all(
        programOrgs?.map((org) => calcOrgUsage(org.id)) || []
      );
      return Object.fromEntries(
        usages.map((usage) => Object.entries(usage)).flat()
      );
    },
    enabled: !!programOrgs && programOrgs.length > 0,
  });

  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ["balance", program.id],
    queryFn: async () => {
      const balances = await Promise.all(
        programOrgs?.map((org) => calcOrgBalance(org.id)) || []
      );
      return Object.fromEntries(
        balances.map((balance) => Object.entries(balance)).flat()
      );
    },
    enabled: !!programOrgs && programOrgs.length > 0,
  });

  const { data: usageOverviewData, isLoading: usageOverviewLoading } = useQuery(
    {
      queryKey: ["usage-overview", program.id, dateRange],
      queryFn: async () => {
        const overviews = await Promise.all(
          programOrgs?.map((org) => getUsageOverview(org)) || []
        );
        return Object.fromEntries(
          overviews.map((overview) => Object.entries(overview)).flat()
        );
      },
      enabled: !!programOrgs && programOrgs.length > 0,
    }
  );

  const filteredOrgs = useMemo(() => {
    return programOrgs?.filter(
      (org) =>
        org.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        org.Name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [programOrgs, searchValue]);

  useEffect(() => {
    if ((!orgsLoading && programOrgs?.length === 0) || orgsError) {
      setIsLoading(false);
    } else {
      setIsLoading(
        balanceLoading || orgsLoading || usageLoading || usageOverviewLoading
      );
    }
  }, [
    balanceLoading,
    programOrgs,
    orgsLoading,
    usageLoading,
    usageOverviewLoading,
  ]);

  useEffect(() => {
    if (usageData) {
      setTotalHours(
        Object.values(usageData).reduce((acc, curr) => acc + curr.totalTime, 0)
      );
    }
  }, [usageData, dateRange]);

  useEffect(() => {
    if (balanceData) {
      setTotalBalance(
        Object.values(balanceData).reduce((acc, curr) => acc + curr, 0)
      );
    }
  }, [balanceData]);

  useEffect(() => {
    // Reset filter(s) when program changes
    setCloudAwardDate(undefined);
  }, [program.id]);

  const calcOrgBalance = async (
    orgId: string
  ): Promise<Record<string, number>> => {
    let max = 0;
    const res = await agent.BillingCredits.getBillingProfile(orgId);
    const balance = (res.data?.credit_details?.remaining_credits || 0) / 100;

    const instanceTypes = await agent.Instances.getAllInstanceTypesAvailable(
      orgId
    );
    const pricedInstanceTypes = (
      (instanceTypes.data?.allInstanceTypes as GPUInstanceType[]) || []
    )
      .filter((instance) => instance.base_price)
      .sort(
        (a, b) => Number(a.base_price!.amount) - Number(b.base_price!.amount)
      );

    // Although orgs may be allowed to use multiple instance types, Academic Grant program orgs
    // will typically only allow a single instance type and we don't want the admin dashboard to
    // show a range. Therefore, we always use the least expensive instance type to
    // calculate remaining balance.
    const leastExpensive = pricedInstanceTypes[0] || null;
    if (leastExpensive) {
      max = Number(
        (balance / Number(leastExpensive.base_price!.amount)).toFixed(1)
      );
    }
    return { [orgId]: max };
  };
  const calcOrgUsage = async (orgId: string) => {
    let totalTime = 0;
    const res = await agent.Organizations.getUsage(
      orgId,
      dateRange as { from: Date; to: Date }
    );
    if (res.success && res.data) {
      const envIds = new Set<string>();
      res.data.Usage.forEach((usage) => {
        if (!envIds.has(usage.EnvID)) {
          // don't double count total time for compute+storage
          totalTime += usage.TotalTime / 3600;
          envIds.add(usage.EnvID);
        }
      });
    }
    return { [orgId]: { totalTime } };
  };
  const exportOrgsAsCsv = () => {
    if (!programOrgs || programOrgs.length === 0) {
      return;
    }

    // Write export data to CSV
    const headers = [
      "Name",
      "GPU Hours (%) Consumed",
      "GPU Hours (k) Consumed",
      "GPU Hours (k) Remaining",
    ];
    const csvData = [
      headers.join(","),
      ...programOrgs.map((org) =>
        [
          org.Name,
          getUsagePercentage(org.id),
          usageData?.[org.id]?.totalTime.toFixed(1) || 0,
          balanceData?.[org.id] || 0,
        ].join(",")
      ),
    ].join("\n");

    // Trigger a download
    const blob = new Blob([csvData], { type: "text/csv" });
    const link = document.createElement("a");
    const startDate = dateRange!.from!.toISOString().split("T")[0];
    const endDate = dateRange!.to!.toISOString().split("T")[0];
    link.href = window.URL.createObjectURL(blob);
    link.download = `${program.id}_${startDate}-${endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };
  const getProgramOrgs = async () => {
    setTotalHours(0);
    setTotalBalance(0);
    const params = new URLSearchParams({ programIds: program.id });
    if (cloudAwardDate) {
      // Convert to non-standard format used by Academic Grant program
      const formattedDate = `${
        cloudAwardDate.getMonth() + 1
      }/${cloudAwardDate.getDate()}/${cloudAwardDate.getFullYear()}`;
      params.set("label", `Cloud Award Date:${formattedDate}`);
    }
    const res = await agent.Admin.listOrgs(params);
    if (!res.success || !res.data) {
      throw new Error("Failed to fetch program orgs");
    }
    return res.data;
  };
  const getUsageOverview = async (org: AdminOrganizationType) => {
    let overview: UsageOverview[] = [];
    const res = await agent.Organizations.getUsageOverview(
      org.id,
      dateRange as { from: Date; to: Date }
    );
    if (res.success && res.data) {
      overview = res.data.Usage;
    }
    return { [org.Name]: overview };
  };

  const getUsagePercentage = (orgId: string) => {
    if (
      usageData?.[orgId]?.totalTime &&
      (!balanceData?.[orgId] || balanceData[orgId] < 1)
    ) {
      return "100%";
    }
    if (usageData?.[orgId]?.totalTime && balanceData?.[orgId]) {
      return `${(
        (usageData[orgId].totalTime / balanceData[orgId]) *
        100
      ).toFixed(1)}%`;
    }
    return "0%";
  };

  if (isLoading) {
    return (
      <Spinner
        className="text-gray-700 dark:text-secondary"
        description="Fetching program data"
        size="medium"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-md text-gray-900 dark:text-white font-semibold">
        Filters
      </h2>
      <div className="flex flex-row gap-10">
        <div className="flex flex-col gap-2 w-full">
          <label className="text-sm text-gray-900 dark:text-white">
            Usage Period
          </label>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            requiredRange={true}
          />
        </div>
        {program.id === PROGRAMS.academicGrant && (
          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm text-gray-900 dark:text-white">
              Cloud Award Date
            </label>
            <DatePicker value={cloudAwardDate} onChange={setCloudAwardDate} />
          </div>
        )}
      </div>
      <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
      <div className="flex flex-row gap-10 text-white">
        <FlatCard className="text-center">
          <label className="text-sm text-gray-700 dark:text-secondary">
            Total Organizations
          </label>
          <h1 className="text-2xl font-bold">{programOrgs?.length || 0}</h1>
        </FlatCard>
        <FlatCard className="text-center">
          <label className="text-sm text-gray-700 dark:text-secondary">
            GPU Hours Consumed
          </label>
          <h1 className="text-2xl font-bold">{displayGpuHours(totalHours)}</h1>
        </FlatCard>
        <FlatCard className="text-center">
          <label className="text-sm text-gray-700 dark:text-secondary">
            GPU Hours Remaining
          </label>
          <h1 className="text-2xl font-bold">
            {totalBalance ? displayGpuHours(totalBalance) : "0.0"}
          </h1>
        </FlatCard>
      </div>
      <UsageOverviewChart usageData={usageOverviewData || {}} />
      <FlatCard>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between">
            <div>
              <div className="flex flex-row items-center gap-2 text-white text-md font-semibold">
                <AcademicCapIcon className="w-5 h-5" />
                <h1 className="text-xl">{program.displayName}</h1>
              </div>
              <p className="text-secondary text-sm">
                View organizations in this program
              </p>
            </div>
            {programOrgs && programOrgs?.length > 0 && (
              <Button
                iconLeft={<ArrowDownTrayIcon className="w-4 h-4 mr-1" />}
                label="Export as CSV"
                size="small"
                type="nvidia"
                onClick={exportOrgsAsCsv}
              />
            )}
          </div>
          <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
          {(!programOrgs || programOrgs.length === 0) && (
            <div className="flex justify-center">
              <div className="text-sm text-gray-700 dark:text-secondary">
                No organizations found
              </div>
            </div>
          )}
          {programOrgs && programOrgs.length > 0 && (
            <>
              <Input
                type="search"
                placeholder="Search by organization name or ID..."
                value={searchValue}
                onChange={(ev) => setSearchValue(ev.target.value)}
              />
              <TableRoot className="whitespace-normal">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Name</TableHeaderCell>
                      <TableHeaderCell>GPU Hours (%) Consumed</TableHeaderCell>
                      <TableHeaderCell>GPU Hours (k) Consumed</TableHeaderCell>
                      <TableHeaderCell>GPU Hours (k) Remaining</TableHeaderCell>
                      <TableHeaderCell>Details</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrgs?.map((organization, index) => (
                      <TableRow
                        key={index}
                        className="cursor-pointer hover:bg-zinc-800"
                      >
                        <TableCell>
                          <span className="text-sm text-gray-500 dark:text-secondary">
                            {organization.Name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500 dark:text-secondary">
                            {getUsagePercentage(organization.id)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500 dark:text-secondary">
                            {displayGpuKHours(
                              usageData?.[organization.id]?.totalTime || 0
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500 dark:text-secondary">
                            {balanceData?.[organization.id]
                              ? displayGpuKHours(balanceData[organization.id])
                              : "N/A"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className="text-sm text-highlight underline cursor-pointer hover:text-highlightLighter"
                            onClick={() => {
                              history.push(
                                `/admin/organizations/id/${organization.id}`
                              );
                            }}
                          >
                            View Details
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableRoot>
            </>
          )}
        </div>
      </FlatCard>
    </div>
  );
};

export default ProgramOverview;
